import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchOwnerWallets, payInvoiceFromWallet, resolveMainWalletForCurrency } from '@/store/slices/wallet';
import { createAiCreditsInvoice, fetchCreditRate } from '@/store/slices/aiCredits';

import { CREDIT_CURRENCY, CREDIT_UNIT_USD, creditsToUsd } from '../utils/credits';
// assessTopupFunding + useDepositSync are generic wallet helpers that happen to live in the acquisition
// folder today; reused verbatim here. (TODO: lift them to nova/utils + nova/hooks so this is not a
// hooks -> pages import.)
import { assessTopupFunding, type TopupFunding } from '../pages/acquisition/components/AdBudgetLimit/topup-funding';
import { useDepositSync } from '../pages/acquisition/components/AdBudgetLimit/useDepositSync';

export type CreditTopup = {
  /** Selected credit amount to buy. */
  credits: number;
  setCredits: (n: number) => void;
  /** Live exchange rate (USD per credit); the fixed fallback until the BE quote loads. */
  rate: number;
  /** Money cost of `credits` at `rate`, in {@link CREDIT_CURRENCY}. */
  moneyCost: number;
  /** The MAIN wallet currency the top-up is funded in (should be CREDIT_CURRENCY when matched). */
  mainCurrency: string;
  /** The MAIN wallet's available balance in that currency (null until the wallet loads). */
  mainBalance: number | null;
  /** Funding verdict of `moneyCost` against the MAIN wallet — decides whether the MAIN rail is offered. */
  funding: TopupFunding;
  /** True when the MAIN wallet is known to cover `moneyCost` (the "pay from Main wallet" rail). */
  canPayFromMain: boolean;
  /** Primary CTA: pay from MAIN when it covers the cost, else open the card sheet. */
  purchase: () => void;
  /** Explicit "pay from Main wallet" rail (mints the invoice, settles it from MAIN, polls the balance). */
  payFromMain: () => void;
  /** Card rail: the consuming sheet renders <CardPaymentSheet> while `cardOpen`. */
  cardOpen: boolean;
  openCard: () => void;
  closeCard: () => void;
  /** Passed to CardPaymentSheet.createIntent — mints the deposit_ai_credits invoice, returns its secret. */
  mintCardIntent: () => Promise<{ clientSecret: string | undefined; invoiceId: string }>;
  /** Called by CardPaymentSheet.onSuccess: start polling the AI_CREDITS balance until the card credit lands. */
  onCardPaid: () => void;
  /** A settle is running (minting/paying) or the balance is still catching up — hold the CTA. */
  settling: boolean;
  syncing: boolean;
  /** How many credits just landed (for a few seconds after a purchase clears). */
  credited: number | null;
  /** A purchase settled but the balance never moved across the poll window. */
  stalled: boolean;
  retrySync: () => void;
  /** A mint/pay is in flight. */
  purchasing: boolean;
  purchaseError: string | null;
};

/**
 * Shared AI-credits buy logic against the owner-hub wallet service. Buying credits mints a
 * `deposit_ai_credits` invoice (priced in fiat at the live rate) and settles it on one of two rails,
 * both crediting the owner-level AI_CREDITS wallet:
 *  - FROM THE MAIN WALLET when it covers the cost (`payInvoiceFromWallet`), or
 *  - BY CARD (the invoice's Stripe intent, driven by CardPaymentSheet).
 * The direct card rail means there is no "top up MAIN first" step — the card pays for the credits
 * outright. After either rail settles, the AI_CREDITS balance (`state.ownerWallet.credits`) is polled
 * until the new credits land. `onPurchased` fires once they do (the caller shows success).
 */
export function useCreditTopup(onPurchased?: () => void): CreditTopup {
  const dispatch = useAppDispatch();
  const ownerWallet = useAppSelector((s) => s.ownerWallet ?? null);
  const walletLoaded = useAppSelector((s) => s.ownerWallet?.loaded ?? false);
  const walletError = useAppSelector((s) => Boolean(s.ownerWallet?.error));
  const creditsBalance = useAppSelector((s) => s.ownerWallet?.credits ?? null);
  const rateUsd = useAppSelector((s) => s.aiCredits?.rateUsd ?? null);
  const rateLoaded = useAppSelector((s) => s.aiCredits?.rateLoaded ?? false);

  const [credits, setCredits] = React.useState(0);
  const [cardOpen, setCardOpen] = React.useState(false);
  const [purchasing, setPurchasing] = React.useState(false);
  const [purchaseError, setPurchaseError] = React.useState<string | null>(null);

  const rate = rateUsd && rateUsd > 0 ? rateUsd : CREDIT_UNIT_USD;
  const moneyCost = creditsToUsd(credits, rate);

  // Credits are USD-priced, so the MAIN rail funds from the USD MAIN wallet.
  const walletView = resolveMainWalletForCurrency(ownerWallet, CREDIT_CURRENCY);
  // Watch the AI_CREDITS balance (not MAIN) — that is the number that moves when a purchase clears.
  const { syncing, credited, stalled, start, retry } = useDepositSync(creditsBalance);

  // A MAIN wallet in another currency is not comparable to a USD cost — the guard then reports 'unknown'.
  const comparable = walletView.matched || (walletView.currency ?? '').toUpperCase() === CREDIT_CURRENCY;
  const funding = assessTopupFunding({
    amount: moneyCost,
    balance: walletView.balance,
    walletLoaded,
    walletError,
    comparable,
  });
  const canPayFromMain = funding.canFund;
  const settling = purchasing || syncing;

  // Balance + funding need the owner's wallets; the rate needs the BE quote. Load both once on mount.
  React.useEffect(() => {
    if (!walletLoaded) void dispatch(fetchOwnerWallets());
  }, [walletLoaded, dispatch]);
  React.useEffect(() => {
    if (!rateLoaded) void dispatch(fetchCreditRate());
  }, [rateLoaded, dispatch]);

  const openCard = React.useCallback(() => setCardOpen(true), []);
  const closeCard = React.useCallback(() => setCardOpen(false), []);

  // The pre-purchase credit balance, so the poll knows what "moved" means (null = no wallet yet -> 0).
  const settleBaseline = React.useCallback(
    (): number => (walletLoaded && creditsBalance !== null ? Math.max(0, creditsBalance) : 0),
    [walletLoaded, creditsBalance],
  );

  // Mint the deposit_ai_credits invoice; returns its Stripe secret for the card rail.
  const mintCardIntent = React.useCallback(async (): Promise<{ clientSecret: string | undefined; invoiceId: string }> => {
    const r = await dispatch(createAiCreditsInvoice({ ai_credits_amount: credits, currency: CREDIT_CURRENCY })).unwrap();
    return { clientSecret: r.clientSecret, invoiceId: r.invoice_id };
  }, [dispatch, credits]);

  // The card charge cleared (Stripe confirmed) — poll the AI_CREDITS balance until the webhook credits it.
  const onCardPaid = React.useCallback(() => {
    setCardOpen(false);
    start(settleBaseline());
  }, [start, settleBaseline]);

  // Pay the credits invoice from the MAIN wallet: mint it, settle it from MAIN, then poll the balance.
  const runPayFromMain = React.useCallback(async (): Promise<void> => {
    if (purchasing || credits <= 0 || !canPayFromMain) return;
    const baseline = settleBaseline();
    setPurchasing(true);
    setPurchaseError(null);
    try {
      const invoice = await dispatch(createAiCreditsInvoice({ ai_credits_amount: credits, currency: CREDIT_CURRENCY })).unwrap();
      await dispatch(payInvoiceFromWallet({ invoice_id: invoice.invoice_id })).unwrap();
      // Don't fire `onPurchased` yet — poll first; the credited-watch effect fires it once the
      // AI_CREDITS balance actually moves (same signal the card rail uses), so success is honest.
      start(baseline);
    } catch (e) {
      setPurchaseError((e as { error?: string } | undefined)?.error ?? null);
    } finally {
      setPurchasing(false);
    }
  }, [purchasing, credits, canPayFromMain, settleBaseline, dispatch, start]);

  const purchase = React.useCallback(() => {
    if (credits <= 0) return;
    if (canPayFromMain) void runPayFromMain();
    else setCardOpen(true);
  }, [credits, canPayFromMain, runPayFromMain]);

  // Fire `onPurchased` for the card rail too, once the polled balance actually moves.
  const notifiedRef = React.useRef(false);
  React.useEffect(() => {
    if (credited != null && credited > 0 && !notifiedRef.current) {
      notifiedRef.current = true;
      onPurchased?.();
    }
    if (credited == null) notifiedRef.current = false;
  }, [credited, onPurchased]);

  return {
    credits,
    setCredits,
    rate,
    moneyCost,
    mainCurrency: walletView.currency,
    mainBalance: walletView.balance,
    funding,
    canPayFromMain,
    purchase,
    payFromMain: () => void runPayFromMain(),
    cardOpen,
    openCard,
    closeCard,
    mintCardIntent,
    onCardPaid,
    settling,
    syncing,
    credited,
    stalled,
    retrySync: retry,
    purchasing,
    purchaseError,
  };
}

export default useCreditTopup;
